<template>
  <div>
    <div>
      <b-field label="Actions">
        <b-select v-model="fields.action">
          <option v-for="action in actions" :key="action.key" :value="action.key">
            {{ action.label }}
          </option>
        </b-select>
      </b-field>
      <b-field v-show="showUpdateCheckboxes">
        <b-checkbox-button
          v-for="update in updates"
          :key="update.key"
          v-model="fields.updates"
          type="is-primary is-light"
          :native-value="update.key"
        >
          <span>{{ update.label }}</span>
        </b-checkbox-button>
      </b-field>
    </div>
    <hr v-show="Object.values(this.fields).find((field) => field.show)" />
    <div v-show="fields.updates.length > 0">
      <b-field v-show="fields.radius.show" label="Rayon">
        <o-dropdown
          v-model="fields.radius.value"
          :items="radiuses"
          @input="fields.radius.value = $event"
        >
          <template #label="{ label }">
            {{ radius(label) }}
          </template>
          <template #item="{ item }">
            {{ radius(item) }}
          </template>
        </o-dropdown>
      </b-field>
      <b-field
        v-show="fields.liveCategoryIds.show"
        grouped
        group-multiline
        label="Catégories"
        :message="fields.liveCategoryIds.error"
        :type="fields.liveCategoryIds.error !== null ? 'is-danger' : ''"
      >
        <b-checkbox-button
          v-for="category in liveCategories"
          :key="category.id"
          v-model="fields.liveCategoryIds.value"
          :native-value="category.id"
          :type="`is-${category.color}`"
        >
          {{ category.name }}
        </b-checkbox-button>
      </b-field>
      <b-field v-show="fields.color.show" grouped group-multiline label="Couleur">
        <b-radio-button
          v-for="color in colors"
          :key="color.value"
          v-model="fields.color.value"
          :native-value="color.value"
          :type="`is-${fields.color.value}`"
        >
          {{ color.label }}
        </b-radio-button>
      </b-field>
      <b-field v-show="fields.icon.show" label="Icône">
        <o-icon-picker v-model="fields.icon.value" :icons="icons" />
      </b-field>
      <b-field v-show="fields.accessType.show" label="Accès">
        <o-dropdown
          v-model="fields.accessType.value"
          :items="accessTypes"
          @input="fields.accessType.value = $event"
        >
          <template #label="{ label }">
            <b-icon
              size="is-small"
              :type="accessTypeIcons[label].type"
              :icon="accessTypeIcons[label].icon"
            />
          </template>
          <template #item="{ item }">
            <b-icon
              size="is-small"
              :type="accessTypeIcons[item].type"
              :icon="accessTypeIcons[item].icon"
            />
          </template>
        </o-dropdown>
      </b-field>
    </div>
    <hr />
    <b-field>
      <b-button type="is-primary" :loading="loading" :disabled="disabledSubmit" @click="submit">
        {{ actions[fields.action].label }} la sélection
      </b-button>
    </b-field>
  </div>
</template>

<script>
import { LIVE_STAGE_WAYPOINT_COLORS } from '@constants/waypoint/color'
import { RADIUSES } from '@constants/waypoint/radiuses'
import {
  WAYPOINT_ACCESS_TYPE_ICONS,
  WAYPOINT_ACCESS_TYPE_PRIVATE,
  WAYPOINT_ACCESS_TYPE_PUBLIC,
} from '@constants/waypoint/accessType'
import ODropdown from '@components/Dropdown'
import { iconNames } from '@data/icons'
import OIconPicker from '@components/IconPicker.vue'

const UPDATE_ACTION = 'update'
const DELETE_ACTION = 'delete'

const COLOR = 'color'
const ICON = 'icon'
const RADIUS = 'radius'
const CATEGORY = 'liveCategoryIds'
const ACCESS_TYPE = 'accessType'

export default {
  name: 'LiveStageWaypointForm',

  components: { OIconPicker, ODropdown },

  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    liveCategories: {
      type: Array,
      required: true,
    },
    liveStageWaypoints: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      fields: {
        action: UPDATE_ACTION,
        updates: [],
        color: {
          show: false,
          value: LIVE_STAGE_WAYPOINT_COLORS[0],
        },
        icon: {
          show: false,
          value: null,
        },
        radius: {
          show: false,
          value: RADIUSES[0],
        },
        accessType: {
          show: false,
          value: WAYPOINT_ACCESS_TYPE_PUBLIC,
        },
        liveCategoryIds: {
          show: false,
          value: [],
          error: null,
        },
      },
    }
  },

  created() {
    this.colors = LIVE_STAGE_WAYPOINT_COLORS
    this.icons = iconNames
    this.radiuses = [null, ...RADIUSES]
    this.accessTypeIcons = WAYPOINT_ACCESS_TYPE_ICONS
    this.accessTypes = [WAYPOINT_ACCESS_TYPE_PUBLIC, WAYPOINT_ACCESS_TYPE_PRIVATE]

    this.updates = {
      [COLOR]: { key: COLOR, label: 'Couleur' },
      [ICON]: { key: ICON, label: 'Icône' },
      [RADIUS]: { key: RADIUS, label: 'Rayon' },
      [CATEGORY]: { key: CATEGORY, label: 'Catégorie' },
      [ACCESS_TYPE]: { key: ACCESS_TYPE, label: 'Accès' },
    }

    this.actions = {
      [UPDATE_ACTION]: { key: UPDATE_ACTION, label: 'Modifier' },
      [DELETE_ACTION]: { key: DELETE_ACTION, label: 'Supprimer' },
    }
  },

  watch: {
    'fields.action'() {
      this.fields.updates = []
    },

    'fields.updates'() {
      this.fields.color.show = this.fields.updates.indexOf(COLOR) !== -1
      this.fields.icon.show = this.fields.updates.indexOf(ICON) !== -1
      this.fields.radius.show = this.fields.updates.indexOf(RADIUS) !== -1
      this.fields.liveCategoryIds.show = this.fields.updates.indexOf(CATEGORY) !== -1
      this.fields.accessType.show = this.fields.updates.indexOf(ACCESS_TYPE) !== -1
    },

    'fields.radius.show'(show) {
      if (show && this.fields.liveCategoryIds.error) {
        this.fields.liveCategoryIds.error = null
      }
    },

    'fields.radius.value'(radius) {
      this.fields.liveCategoryIds.error =
        !radius && this.fields.liveCategoryIds.value.length ? 'Rayon requis' : null
    },

    async 'fields.liveCategoryIds.show'() {
      await this.validateLiveCategoryId()
    },

    async 'fields.liveCategoryIds.value'() {
      await this.validateLiveCategoryId()
    },

    async 'liveStageWaypoints.length'() {
      await this.validateLiveCategoryId()
    },
  },

  computed: {
    disabledSubmit() {
      return (
        this.loading ||
        this.fields.liveCategoryIds.error ||
        (this.fields.action === UPDATE_ACTION && this.fields.updates.length === 0)
      )
    },

    showUpdateCheckboxes() {
      return this.fields.action === UPDATE_ACTION
    },

    waypointsWithRole() {
      return this.liveStageWaypoints.filter((waypoint) => waypoint.role !== null)
    },
  },

  methods: {
    submit() {
      if (this.fields.action === DELETE_ACTION) {
        this.$emit('delete')
        return
      }

      const fields = this.fields.updates.reduce(
        (updates, update) => ({ ...updates, [update]: this.fields[update].value }),
        {},
      )

      this.$emit('submit', fields)
    },

    radius(radius) {
      return radius ? `${radius}m` : '—'
    },

    async fetchAllAvailableRoles(waypoint) {
      this.availableRoles = await this.$services.liveStageWaypointService.getAllAvailableRoles(
        waypoint.liveStageId,
        this.fields.liveCategoryIds.value,
        waypoint.id,
      )
    },

    async validateLiveCategoryId() {
      if (!this.fields.liveCategoryIds.show) {
        return
      }

      if (!this.waypointsWithRole.length) {
        this.fields.liveCategoryIds.error = null

        return
      }

      const unavailableWaypointNames = []
      for (const waypoint of this.waypointsWithRole) {
        const availableRoles = await this.$services.liveStageWaypointService.getAllAvailableRoles(
          waypoint.liveStageId,
          this.fields.liveCategoryIds.value,
          waypoint.id,
        )

        if (!availableRoles.includes(waypoint.role)) {
          unavailableWaypointNames.push(waypoint.name)
        }
      }

      if (!unavailableWaypointNames.length) {
        this.fields.liveCategoryIds.error = null

        return
      }

      const joinNames = unavailableWaypointNames.join(', ')
      if (this.fields.liveCategoryIds.value.length) {
        this.fields.liveCategoryIds.error = `Catégorie indisponible pour : ${joinNames}`

        return
      }

      this.fields.liveCategoryIds.error = `Catégorie requise pour : ${joinNames}`
    },
  },
}
</script>
